// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { of, Observable } from 'rxjs';
import { delay, catchError } from 'rxjs/operators';

// --- 3rd Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { Constants, ApplicationError, RespondentEmailValidationModel, RespondentModel, ErrorMessage } from '../../../models';
import { environment } from '../../../../environments/environment';
import { AppService } from '../app.service';
import { RespondentsBaseService,  } from './respondents-base.service';
import { ParticipantGroupsBaseService } from './participant-groups-base.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class RespondentsService extends RespondentsBaseService {

  constructor(
    private httpClient: HttpClient,
    private translateService: TranslateService,
    private appService: AppService,
    groupsService: ParticipantGroupsBaseService
  ) {
    super(groupsService);
  }

  checkForRespondentEmailBounce(model: RespondentEmailValidationModel): void {
    if (!model) {
      return;
    }

    const url = environment.apiConfig.surveyApiUrl + environment.apiConfig.validateRespondentEmailApi;
    this.httpClient.post<RespondentEmailValidationModel>(url, model).pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._validateError.bind(this))
    ).subscribe(result => {
      this._respondentBounceCheck$.next(result);
    });
  }

  override validateRespondentEmailAddressV2(model: RespondentEmailValidationModel): void {
    if (!model.onlyCheckBounced) {
      if (model.errorMessage) {
        model.isValid = false;
        this._respondentValidationResult$.next(model);
        return;
      }

      if (!model.isValid) {
        const isValidAddOrUpdateRequest = this.groupsService.validateAddOrUpdateLocally(model);
        if (!isValidAddOrUpdateRequest) {
          model.isValid = false;
          this._respondentValidationResult$.next(model);
          return;
        }
      }
    }

    const url = environment.apiConfig.surveyApiUrl + environment.apiConfig.validateRespondentEmailApi;
    this.httpClient.post<RespondentEmailValidationModel>(url, model).pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._validateError.bind(this))
    )
    .subscribe((m: RespondentEmailValidationModel) => {
      this._respondentValidationResult$.next(m);
    });
  }

  override clearValidateRespondentEmailAddressV2(): void {
    this._respondentValidationResult$.next(null);
  }

  validateRespondentEmailAddress(model: RespondentEmailValidationModel): void {
    if (!model.onlyCheckBounced) {
      const isValidAddOrUpdateRequest = this.groupsService.validateAddOrUpdateLocally(model);
      if (!isValidAddOrUpdateRequest) {
        model.isValid = false;
        this.groupsService.respondentValidated(model);
        return;
      }
    }

    const url = environment.apiConfig.surveyApiUrl + environment.apiConfig.validateRespondentEmailApi;
    this.httpClient.post<RespondentEmailValidationModel>(url, model).pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._validateError.bind(this))
    )
    .subscribe((m: RespondentEmailValidationModel) => {
      this.groupsService.respondentValidated(m);
      if (m.isValid) {
        const theRespondent = this.groupsService.getRespondentFromGroup(m.groupId, m.respondentGroupIndex);

        if (!theRespondent) {
          return;
        }

        theRespondent.isSet = theRespondent.isSet || false;
        theRespondent.personalMessage = '';
        if (m.firstName) {
          theRespondent.firstName = m.firstName;
        }
        if (m.lastName) {
          theRespondent.lastName = m.lastName;
        }
        if (m.languageId) {
          theRespondent.languageId = m.languageId;
          theRespondent.locale = m.locale;
        }
        theRespondent.isSaving = false;
        this.groupsService.refreshRespondent(theRespondent);
      }
    });
  }

  addOrUpdate(model: RespondentModel): void {
    const validationModel = {
      emailAddress: model.emailAddress,
      groupId: model.groupId,
      respondentGroupIndex: model.groupIndex
    } as RespondentEmailValidationModel;
    const isValidAddOrUpdateRequest = this.groupsService.validateAddOrUpdateLocally(validationModel);

    if (!isValidAddOrUpdateRequest) {
      model.isValid = false;
      model.errorMessage = validationModel.errorMessage;
      this.groupsService.refreshRespondent(model);
      return;
    }

    if (!this.groupsService.isGroupSet(model.groupId)) {
      return;
    }

    const url = environment.apiConfig.surveyApiUrl + environment.apiConfig.respondentsApi;

    if (!model.id) {
      this._addRespondent(url, model);
    } else {
      this._updateRespondent(url, model);
    }
  }

  getPreviousRespondents(pageSize?: number) {
    if (!pageSize) {
      pageSize =  Constants.DEFAULT_PAGE_SIZE;
    }

    this.appService.setLoading(true);
    const url = environment.apiConfig.surveyApiUrl + environment.apiConfig.previousRespondentsApi + `?pagesize=${pageSize}`;
    this.httpClient.get<RespondentModel[]>(url).pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._previousRespondentsError.bind(this))
    ).subscribe(result => {
      this._previousRespondents$.next(result);
      this.appService.setLoading(false);
    });
  }

  override delete(respondent: RespondentModel): void {
    if (!respondent || !respondent.id || !respondent.isSet) {
      return;
    }

    const respondentIndex = respondent.groupIndex;

    // all respondents in the group after the deleted respondent need to have their group index decremented - get the list of respondents.
    this.groupsService.participantGroups$
  }

  private _validateError(err: any): Observable<RespondentEmailValidationModel> {

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'RespondentsService',
      false,
      err
    );

    this.appService.setLastError(error);

    return of({
      isValid: false
    } as RespondentEmailValidationModel);
  }

  private _addRespondent(url: string, model: RespondentModel) {
    this._respondentValidationResult$.next(null);
    this.httpClient.post<RespondentModel>(url, model).pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._postError.bind(this, model))
    )
    .subscribe((m: RespondentModel) => {
      m.isExpanded = false;
      m.isSaving = false;
      this.groupsService.respondentUpdated(m);
      this.groupsService.expandNextRespondent();
    });
  }

  private _updateRespondent(url: string, model: RespondentModel) {
    url = `${url}/${model.id}`;
    this.httpClient.put<RespondentModel | null>(url, model).pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._putError.bind(this, model))
    )
    .subscribe((respondent: RespondentModel | null) => {

      if (!respondent) {
        return;
      }

      model.isSaving = false;
      model.isExpanded = false;
      model.id = respondent.id;
      this.groupsService.respondentUpdated(model);
    });
  }

  private _postError(model: RespondentModel, err: HttpErrorResponse): Observable<RespondentModel> {

    const errorCode: number = err.status === 409 ?
      ErrorMessage.respondentManagementIndexConflictErrorCode:
      ErrorMessage.genericErrorCode;

    const error: ApplicationError = ApplicationError.create(
      errorCode,
      'RespondentsService',
      false,
      err
    );

    this.appService.setLastError(error);

    model.isValid = false;
    return of(model);
  }

  private _putError(model: RespondentModel, err: HttpErrorResponse): Observable<RespondentModel | null> {

    const errorCode: number = err.status === 409 ?
      ErrorMessage.respondentManagementIndexConflictErrorCode:
      ErrorMessage.genericErrorCode;

    const error: ApplicationError = ApplicationError.create(
      errorCode,
      'RespondentsService',
      false,
      err
    );

    this.appService.setLastError(error);

    model.isValid = false;
    return of(null);
  }

  private _previousRespondentsError(err: any) {

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'RespondentsService',
      false,
      err
    );

    this.appService.setLastError(error);

    return of([] as RespondentModel[]);
  }

}
